import { type Article } from '../models/articles';
import { CreatePayoutPage } from '../pages/CreatePayoutPage';
import { DecryptAndVerifyPage } from '../pages/DecryptAndVerifyPage';
import { IntroductionPage } from '../pages/IntroductionPage';
import { IsoPage } from '../pages/IsoPage';
import { ReasonCodes } from '../pages/ReasonCodesPages';
import { ReasonCodesCollection } from '../pages/ReasonCodesCollectionPages';
import { ProductsPage } from '../pages/ProductsPage';
import { SignAndEncryptPage } from '../pages/SignAndEncryptPage';
import { WebhooksPage } from '../pages/WebhooksPage';
import { SwaggerFile } from '../endpoint-specs/endpoints';
import { CertificateContent } from '../components/InpayCertificateLink';
import { VirtualBanksPage } from '../pages/VirtualBanksPage';

export const endpointArticles: Array<Article> = [
  {
    id: 'getting-started',
    label: 'Getting started',
    swaggerFilePath: SwaggerFile.authentication,
    children: [
      {
        id: 'introduction',
        label: 'Introduction',
        component: IntroductionPage
      },
      {
        id: 'security-model',
        label: 'Security model',
        resource: { filePath: 'api/getting-started/security-model' }
      },
      {
        id: 'certificates',
        label: 'Certificates',
        component: CertificateContent
      },
      {
        id: 'sign-and-encrypt',
        label: 'Sign & Encrypt a request',
        component: SignAndEncryptPage
      },
      {
        id: 'decrypt-and-verify',
        label: 'Decrypt & Verify a response',
        component: DecryptAndVerifyPage
      },
      {
        id: 'http-headers',
        label: 'Http Headers',
        content:
          'A few headers are required to make the requests valid for the Inpay API. ',
        children: [
          {
            id: 'X-Auth-Uuid',
            label: 'X-Auth-Uuid',
            resource: {
              filePath: 'api/getting-started/http-headers/x-auth-uuid'
            }
          },
          {
            id: 'X-Request-Id',
            label: 'X-Request-Id',
            resource: {
              filePath: 'api/getting-started/http-headers/x-request-id'
            }
          },
          {
            id: 'authentication',
            label: 'Bearer Authentication',
            resource: {
              filePath: 'api/getting-started/http-headers/bearer-token'
            }
          }
        ]
      },
      {
        id: 'getting-started-endpoints',
        label: 'Endpoints',
        children: [
          {
            id: 'test-authentication',
            label: 'Test authentication',
            endpoint: {
              method: 'GET',
              path: '/api/authentication/checks/credentials'
            }
          },
          {
            id: 'test-decryption',
            label: 'Test decryption ',
            endpoint: {
              method: 'POST',
              path: '/api/authorization/checks/decryption'
            }
          },
          {
            id: 'test-encryption',
            label: 'Test encryption',
            endpoint: {
              method: 'POST',
              path: '/api/authorization/checks/encryption'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'reconciliation',
    label: 'Reconciliation',
    swaggerFilePath: SwaggerFile.reconciliation,
    children: [
      {
        id: 'reconciliation-endpoints',
        label: 'Endpoints',
        children: [
          {
            id: 'reconciliation-get-balance',
            label: 'Get Account Balance',
            endpoint: {
              method: 'GET',
              path: '/reconciliation/accounts/balance/{scheme_name}/{id}'
            }
          },
          {
            id: 'reconciliation-account-funding',
            label: 'Account Funding',
            endpoint: {
              method: 'POST',
              path: '/reconciliation/accounts/funding'
            }
          },
          {
            id: 'reconciliation-daily-statement',
            label: 'Get daily statement',
            endpoint: {
              path: '/reconciliation/accounts/{account_number}/daily_statement.json',
              method: 'GET'
            }
          },
          {
            id: 'reconciliation-monthly-statement',
            label: 'Get monthly statement',
            endpoint: {
              method: 'GET',
              path: '/reconciliation/accounts/{account_number}/monthly_statement.json'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'payouts',
    label: 'Payouts',
    swaggerFilePath: SwaggerFile.payouts,
    content:
      'Payouts enable you to issue cross-border payments using our network of providers spanning the globe!',
    children: [
      {
        id: 'payouts-concepts',
        label: 'Concepts',
        children: [
          {
            id: 'payouts-introduction',
            label: 'Payment request lifecycle',
            resource: { filePath: 'api/payouts-introduction' }
          },
          {
            id: 'payouts-virtual-banks',
            label: 'My virtual bank accounts',
            component: VirtualBanksPage,
            props: { filePath: 'api/payouts_virtual_bank_account' }
          },
          {
            id: 'payouts-products',
            label: 'Products',
            component: ProductsPage,
            props: {
              origin: 'disbursement'
            }
          },
          {
            id: 'payouts-terminology',
            label: 'Terminology',
            resource: { filePath: 'api/payouts-terminology' }
          },
          {
            id: 'payout-reason-codes',
            label: 'Reason codes',
            component: ReasonCodes,
            props: {
              filePath: 'reason-codes-payouts'
            }
          },
          {
            id: 'webhooks',
            label: 'Webhooks',
            component: WebhooksPage,
            props: {
              url: 'webhooks'
            }
          }
        ]
      },
      {
        id: 'payouts-endpoints',
        label: 'Endpoints',
        children: [
          {
            id: 'payouts-get-details',
            label: 'Get Payment status',
            endpoint: {
              swaggerFile: SwaggerFile.payouts,
              method: 'GET',
              path: '/disbursement/payment_requests/{inpay_unique_reference}'
            }
          },
          {
            id: 'payouts-search',
            label: 'Search a payment request',
            endpoint: {
              method: 'GET',
              path: '/disbursement/payment_requests/search'
            }
          },
          {
            id: 'payouts-create',
            label: 'Create Payment request⚡',
            component: CreatePayoutPage,
            endpoint: {
              method: 'POST'
            }
          },
          {
            id: 'external-codes',
            label: 'Find external codes',
            endpoint: {
              method: 'GET',
              path: '/disbursement/external_codes/{external_code_set_name}'
            }
          },
          {
            id: 'reports',
            label: 'Create Payment Request Report',
            endpoint: {
              method: 'POST',
              path: '/disbursement/payment_requests/reports'
            }
          },
          {
            id: 'reports-check-state',
            label: 'Check Payment Request Report State',
            endpoint: {
              method: 'GET',
              path: '/disbursement/payment_requests/reports/check_state'
            }
          },
          {
            id: 'reports-download',
            label: 'Download Payment Request Report',
            endpoint: {
              method: 'GET',
              path: '/disbursement/payment_requests/reports/download'
            }
          }
        ]
      },
      {
        id: 'payouts-iso',
        label: 'ISO 20022 - XML',
        children: [
          {
            id: 'iso-introduction',
            label: 'Introduction',
            resource: { filePath: 'api/iso-introduction' }
          },
          {
            id: 'iso-request',
            label: 'ISO 20222 Request',
            component: IsoPage,
            endpoint: {
              method: 'POST'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'collections',
    label: 'Payins',
    swaggerFilePath: SwaggerFile.collection,
    children: [
      {
        id: 'payins-concepts',
        label: 'Concepts',
        children: [
          {
            id: 'payins-terminology',
            label: 'Terminology',
            resource: { filePath: 'api/collections/terminology' }
          },
          {
            id: 'payins-lifecycle',
            label: 'Order lifecycle',
            resource: { filePath: 'api/collections/order-lifecycle' }
          },
          {
            id: 'payins-virtual-banks',
            label: 'My virtual bank accounts',
            component: VirtualBanksPage,
            props: { filePath: 'api/payins_virtual_bank_account' }
          },
          {
            id: 'payins-products',
            label: 'Products',
            component: ProductsPage,
            props: {
              origin: 'collection'
            }
          },
          {
            id: 'payins-status-reasons',
            label: 'Order status reasons',
            resource: { filePath: 'api/collections/order-status-reasons' }
          },
          {
            id: 'payins-instant-bank-transfer-payment-status',
            label: 'Instant bank transfer payment status',
            resource: {
              filePath: 'api/collections/instant-bank-transfer-payment-status'
            }
          },
          {
            id: 'payins-bank-transfer-payment-status',
            label: 'Bank transfer payment status',
            resource: {
              filePath: 'api/collections/bank-transfer-payment-status'
            }
          },
          {
            id: 'wallet-transfer-status',
            label: 'Wallet transfer status',
            resource: {
              filePath: 'api/collections/wallet-transfer-status.md'
            }
          },
          {
            id: 'payins-ui-integration',
            label: 'UI integration',
            resource: {
              filePath: 'api/collections/ui-integration'
            }
          },
          {
            id: 'payins-reason-codes',
            label: 'Reason codes',
            component: ReasonCodesCollection,
            props: {
              filePath: 'reason-codes-collection'
            }
          },
          {
            id: 'collection-webhooks',
            label: 'Webhooks',
            component: WebhooksPage,
            props: {
              url: 'collection-webhooks'
            }
          }
        ]
      },
      {
        id: 'payins-endpoints',
        label: 'Endpoints',
        children: [
          {
            id: 'collections-order-create',
            label: 'Create order',
            endpoint: {
              method: 'POST',
              path: '/api/collection/orders'
            }
          },
          {
            id: 'collections-order-get',
            label: 'Get Order details',
            endpoint: {
              method: 'GET',
              path: '/api/collection/orders/{order_uuid}'
            }
          },
          {
            id: 'collections-order-cancel',
            label: 'Cancel order',
            endpoint: {
              method: 'DELETE',
              path: '/api/collection/orders/{order_uuid}'
            }
          },
          {
            id: 'collections-payment-create',
            label: 'Create payment',
            endpoint: {
              method: 'POST',
              path: '/api/collection/orders/{order_uuid}/payments'
            }
          },
          {
            id: 'collections-payment-methods-get',
            label: 'Get payment methods',
            endpoint: {
              method: 'GET',
              path: '/api/collection/orders/{order_uuid}/payment_methods'
            }
          },
          {
            id: 'collections-get-payment-details',
            label: 'Get payment details',
            endpoint: {
              method: 'GET',
              path: '/api/collection/orders/{order_uuid}/payments/{uuid}'
            }
          },
          {
            id: 'collections-post-refund-order',
            label: 'Refund order',
            endpoint: {
              method: 'POST',
              path: '/api/collection/orders/{uuid}/refund'
            }
          },
          {
            id: 'collections-instant-bank',
            label: 'Instant Bank Transfer',
            content: 'Instant Bank Transfer',
            children: [
              {
                id: 'collections-banks-get',
                label: 'Get banks',
                endpoint: {
                  method: 'GET',
                  path: '/api/collection/orders/{order_uuid}/payments/{payment_uuid}/banks'
                }
              },
              {
                id: 'collections-assign-bank',
                label: 'Assign bank',
                endpoint: {
                  method: 'POST',
                  path: '/api/collection/orders/{order_uuid}/payments/{payment_uuid}/assign_bank'
                }
              },
              {
                id: 'collections-get-debtor-bank-details',
                label: 'Get debtor bank details',
                endpoint: {
                  method: 'GET',
                  path: '/api/collection/instant_bank_transfer/banks/{debtor_bank_uuid}'
                }
              },
              {
                id: 'collections-extra-fields-get',
                label: 'Get extra fields',
                endpoint: {
                  method: 'GET',
                  path: '/api/collection/orders/{order_uuid}/payments/{payment_uuid}/banks/{bank_external_id}/extra_fields'
                }
              },
              {
                id: 'collections-create-extra-fields',
                label: 'Create extra fields',
                endpoint: {
                  method: 'POST',
                  path: '/api/collection/orders/{order_uuid}/payments/{payment_uuid}/extra_fields'
                }
              }
            ]
          },
          {
            id: 'collections-bank',
            label: 'Bank Transfer',
            content: 'Bank Transfer',
            children: [
              {
                id: 'colletions-assign-debtory-countries',
                label: 'Assign debtor country',
                endpoint: {
                  method: 'POST',
                  path: '/api/collection/orders/{order_uuid}/payments/{payment_uuid}/assign_debtor_country'
                }
              },
              {
                id: 'collections-get-debtor-countries',
                label: 'Get debtor countries',
                endpoint: {
                  method: 'GET',
                  path: '/api/collection/orders/{order_uuid}/payments/{payment_uuid}/debtor_countries'
                }
              },
              {
                id: 'collections-get-bank-transfer-instructions',
                label: 'Get bank transfer instructions',
                endpoint: {
                  method: 'GET',
                  path: '/api/collection/orders/{order_uuid}/payments/{payment_uuid}/bank_transfer_instructions'
                }
              },
              {
                id: 'collections-resolve-overfunding',
                label: 'Resolve overfunding',
                endpoint: {
                  method: 'POST',
                  path: '/api/collection/orders/{order_uuid}/payments/{payment_uuid}/overfunding'
                }
              }
            ]
          },
          {
            id: 'collections-wallet-transfer',
            label: 'Wallet Transfer',
            content: 'Wallet transfer',
            children: [
              {
                id: 'collections-wallet-transfer-get-banks',
                label: 'Get banks',
                endpoint: {
                  method: 'GET',
                  path: '/api/collection/wallet_transfer/banks'
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'fx',
    label: 'FX',
    swaggerFilePath: SwaggerFile.fx,
    children: [
      {
        id: 'fx-terminology',
        label: 'Terminology',
        resource: {
          filePath: 'api/fx'
        }
      },
      {
        id: 'fx-get-exchange-rate',
        label: 'Get FX rate',
        endpoint: {
          method: 'GET',
          path: '/api/fx/exchange_rate'
        }
      }
    ]
  },
  {
    id: 'api-errors',
    label: 'API errors',
    resource: {
      filePath: 'api/api-errors'
    },
    children: [
      {
        id: 'response-codes',
        label: 'Response Codes',
        resource: {
          filePath: 'api/response-codes'
        }
      }
    ]
  }
];
